import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { EImageVariant } from '@onesource/schemas';
import { enqueueSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import { MdContentCopy } from 'react-icons/md';

const VARIANT_ORDER = [
  ...Object.values(EImageVariant).filter(v => v !== EImageVariant.original),
  EImageVariant.original,
] as EImageVariant[];

function getVariantLabel(variant: EImageVariant): string {
  if (variant === EImageVariant.original) {
    return 'Original';
  }
  const size = variant.replace(/^w/, '');
  return `${size}×${size}`;
}

function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null) {
    return '';
  }
  if (bytes === 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(2)} ${units[i]}`;
}

export type TCopyUrlMenuProps = {
  cdnUrls: Partial<Record<EImageVariant, string>>;
  variantSizes: Partial<Record<string, number>>;
  /** When true, renders just the icon button (no label row). Use for toolbar/header contexts. */
  standalone?: boolean;
  /** Icon size for standalone mode. Defaults to 22. */
  iconSize?: number;
  /** Optional sx overrides for the standalone icon button. */
  buttonSx?: object;
};

export function CopyUrlMenu({ cdnUrls, variantSizes, standalone, iconSize = 22, buttonSx }: TCopyUrlMenuProps) {
  const theme = useTheme();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const availableVariants = VARIANT_ORDER.filter(v => cdnUrls[v]);

  const isSingleVariant = availableVariants.length === 1;

  const handleCopy = (variant: EImageVariant) => {
    navigator.clipboard.writeText(cdnUrls[variant]!);
    enqueueSnackbar('URL copied', { variant: 'success', autoHideDuration: 1500 });
    setOpen(false);
  };

  const handleSingleCopy = () => handleCopy(availableVariants[0]);

  const menu = (
    <Menu
      anchorEl={anchorRef.current}
      open={open}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        // borderRadius: `2px !important`,
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 180,
            mt: 0.5,
          },
        },
      }}
    >
      {availableVariants.map(variant => (
        <MenuItem
          key={variant}
          onClick={() => handleCopy(variant)}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.5 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
            {getVariantLabel(variant)}
          </Typography>
          {variantSizes[variant] !== null && (
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.72rem' }}>
              {formatBytes(variantSizes[variant]!)}
            </Typography>
          )}
        </MenuItem>
      ))}
    </Menu>
  );

  if (standalone) {
    return (
      <>
        <Tooltip title="Copy URL" placement="bottom" arrow>
          <IconButton
            ref={anchorRef}
            sx={
              buttonSx ?? {
                color: theme.palette.text.secondary,
                '&:hover': { color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }
            }
            onClick={isSingleVariant ? handleSingleCopy : () => setOpen(true)}
          >
            <MdContentCopy size={iconSize} />
          </IconButton>
        </Tooltip>
        {!isSingleVariant && menu}
      </>
    );
  }

  return (
    <>
      <Tooltip title={isSingleVariant ? 'Copy URL' : 'Choose size to copy'} placement="top" arrow>
        <IconButton
          ref={anchorRef}
          size="small"
          sx={{
            mt: '-2px',
            p: 0.5,
            flexShrink: 0,
            color: theme.palette.text.disabled,
            transition: 'color 0.15s, background-color 0.15s',
            '&:hover': {
              color: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
          onClick={isSingleVariant ? handleSingleCopy : () => setOpen(true)}
        >
          <MdContentCopy size={14} />
        </IconButton>
      </Tooltip>
      {!isSingleVariant && menu}
    </>
  );
}
